// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//
import ProductAction, { ProductActionsStyle } from "@adl/App/Components/Product/ProductActions";
import productDescriptionStyles from "@adl/App/SharedStyle/ProductDescriptionTextStyles";
import { ellipsis, xIsAuthenticated } from "@adl/XDL/Utilities";
import { mergeWithCss } from "@adl/XDL/Utilities/mergeWithCss";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import { ProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import updateProductInfo from "@insite/client-framework/Store/Components/ProductInfoList/Handlers/UpdateProductInfo";
import { getProductInfoFromList } from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import { ProductAvailabilityStyles } from "@insite/content-library/Components/ProductAvailability";
import ProductContextAvailability from "@insite/content-library/Components/ProductContextAvailability";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import ProductPrice from "@insite/content-library/Components/ProductPrice";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    carouselId: string;
    product: ProductModel;
    extendedStyles?: MiniProductCarouselProductStyles;
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => ({
    productInfo: getProductInfoFromList(state, ownProps.carouselId, ownProps.product.id),
    isAuthenticated: xIsAuthenticated(state),
});

const mapDispatchToProps = {
    updateProductInfo,
};

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

export interface MiniProductCarouselProductStyles {
    imageAndInfoContainer?: InjectableCss;
    infoContainer?: InjectableCss;
    actionsContainer?: InjectableCss;
    productImage?: ProductImageStyles;
    productDescriptionStyles?: ProductDescriptionStyles;
    signInButton?: ButtonPresentationProps;
    availabilityStyles?: ProductAvailabilityStyles;
    productActionStyle?: ProductActionsStyle;
    pricingWrapper?: InjectableCss;
}

const miniProductStyles: MiniProductCarouselProductStyles = {
    imageAndInfoContainer: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            align-items: flex-start;

            height: 100%;
        `,
    },
    infoContainer: {
        css: css`
            flex: 1;
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            height: 100%;
        `,
    },
    actionsContainer: {
        css: css`
            display: flex;
            flex-grow: 1;
            justify-content: flex-end;
            margin-top: 10px;
            flex-direction: column;
            width: 100%;
            box-sizing: border-box;
        `,
    },
    productImage: {
        linkWrappingImage: {
            css: css`
                width: 100%;
                height: 85px;
                max-height: 85px;
                width: 85px;
                max-width: 85px;
                margin-right: 10px;
                flex-basis: 85px;
            `,
        },
        img: {
            width: 85,
            height: 85,
            css: css`
                height: auto;
                width: 100%;
                max-height: 100%;
                overflow: hidden;
            `,
        },
    },
    productDescriptionStyles: {
        ...productDescriptionStyles("medium", {
            productDetailLink: {
                typographyProps: {
                    css: css`
                        ${wrapInContainerStyles}
                        ${ellipsis(3)}
                    `,
                },
                css: css`
                    width: 100%;
                    margin-bottom: 10px;
                    min-height: 52px; // maximum height of 3 lines
                `,
            },
            productDetailDescription: {
                css: css`
                    ${wrapInContainerStyles}
                    width: 100%;
                    margin-bottom: 10px;
                    ${ellipsis(3)}
                `,
            },
        }),
    },
    signInButton: {
        sizeVariant: "small",
        css: css`
            flex-grow: 1;
            flex-shrink: 1;
        `,
    },
    productActionStyle: {
        addToCartContainer: {
            css: css`
                > div[class^="GridOffset-sc"] {
                    justify-content: flex-end;
                }
            `,
        },
        productQuantityOrderedStyles: {
            actionsStyles: {
                quantityOrderedTextField: {
                    sizeVariant: "small",
                },
                actionClickable: {
                    css: css`
                        padding: 0 6px;
                    `,
                },
            },
        },

        productAddToCartButtonStyles: {
            sizeVariant: "small",
        },
        qtyWrapper: {
            css: css`
                margin-right: 8px;
                flex: 1;
            `,
        },
        addToCartButtonWrapper: {
            css: css`
                flex-grow: unset;
            `,
        },
    },
    pricingWrapper: {
        css: css`
            min-height: 38px; // include price label height
        `,
    },
    availabilityStyles: {
        container: {
            css: css`
                margin-bottom: 10px;
            `,
        },
    },
};

const MiniProductCarouselProduct: FC<Props> = ({
    carouselId,
    product,
    productInfo,
    isAuthenticated,
    updateProductInfo,
    extendedStyles,
}) => {
    const [styles] = useState(() => mergeWithCss(miniProductStyles, extendedStyles));

    const updateQuantity = (qtyOrdered: number) => {
        updateProductInfo({ id: carouselId, productId: product.id, qtyOrdered });
    };

    const updateUnitOfMeasure = (unitOfMeasure: string) => {
        updateProductInfo({ id: carouselId, productId: product.id, unitOfMeasure });
    };

    if (!product || !productInfo) {
        return null;
    }

    const productContext = {
        product,
        productInfo,
        onUnitOfMeasureChanged: updateUnitOfMeasure,
        onQtyOrderedChanged: updateQuantity,
    };

    return (
        <ProductContext.Provider value={productContext}>
            <StyledWrapper {...styles.imageAndInfoContainer}>
                <ProductImage product={productContext} extendedStyles={styles.productImage} />
                <StyledWrapper {...styles.infoContainer}>
                    <ProductDescription product={productContext} extendedStyles={styles.productDescriptionStyles} />
                    {isAuthenticated && <ProductPrice showLabel={false} product={productContext} />}
                    <StyledWrapper {...styles.actionsContainer}>
                        {isAuthenticated && (
                            <ProductContextAvailability
                                extendedStyles={styles.availabilityStyles}
                                isProductDetailsPage={false}
                                {...{ hideLink: true }}
                            />
                        )}
                        <ProductAction carouselId={carouselId} extendedStyles={styles.productActionStyle} />
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
        </ProductContext.Provider>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(MiniProductCarouselProduct);
